import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/Campaigns.css';

// Import icons
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiBarChart2, 
  FiPlay, 
  FiPause,
  FiEye,
  FiFilter,
  FiChevronDown,
  FiSearch,
  FiX
} from 'react-icons/fi';

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  // Fetch campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        // Add query parameters for filtering and sorting
        const response = await api.get(`/campaigns`, {
          params: {
            status: filters.status || undefined,
            search: filters.search || undefined,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder
          }
        });
        setCampaigns(response.data);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setError('Failed to load campaigns. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaigns();
  }, [filters]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };
  
  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await api.put(`/campaigns/${campaignId}`, { status: newStatus });
      // Update local state
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(campaign => 
          campaign._id === campaignId 
            ? { ...campaign, status: newStatus } 
            : campaign
        )
      );
    } catch (error) {
      console.error('Error updating campaign status:', error);
      // Show error notification
    }
  };
  
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/campaigns/${campaignId}`);
      // Remove from local state
      setCampaigns(prevCampaigns => 
        prevCampaigns.filter(campaign => campaign._id !== campaignId)
      );
    } catch (error) {
      console.error('Error deleting campaign:', error);
      // Show error notification
    }
  };
  
  const getCampaignStatusClass = (status) => {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Paused':
        return 'status-paused';
      case 'Draft':
        return 'status-draft';
      case 'Completed':
        return 'status-completed';
      default:
        return '';
    }
  };
  
  // Placeholder for empty state
  if (!loading && campaigns.length === 0) {
    return (
      <div className="campaign-container">
        <div className="campaigns-header">
          <h1>Campaigns</h1>
          <Link to="/campaigns/new" className="btn btn-primary">
            <FiPlus className="btn-icon" />
            Create Campaign
          </Link>
        </div>
        
        <div className="empty-state-container">
          <div className="empty-state">
            <div className="empty-state-icon">
              <FiBarChart2 />
            </div>
            <h2>No campaigns yet</h2>
            <p>Create your first email campaign to start engaging with your clients.</p>
            <Link to="/campaigns/new" className="btn btn-primary">
              <FiPlus className="btn-icon" />
              Create First Campaign
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="campaign-container">
      <div className="campaigns-header">
        <h1>Campaigns</h1>
        <Link to="/campaigns/new" className="btn btn-primary">
          <FiPlus className="btn-icon" />
          Create Campaign
        </Link>
      </div>
      
      <div className="campaigns-filters">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search campaigns..."
            className="search-input"
          />
          {filters.search && (
            <button 
              className="search-clear" 
              onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
            >
              <FiX />
            </button>
          )}
        </div>
        
        <div className="filter-container">
          <button 
            className={`filter-toggle ${filterOpen ? 'active' : ''}`} 
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <FiFilter />
            Filters
            <FiChevronDown className={`dropdown-arrow ${filterOpen ? 'open' : ''}`} />
          </button>
          
          {filterOpen && (
            <div className="filter-dropdown">
              <div className="filter-group">
                <label>Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Paused">Paused</option>
                  <option value="Draft">Draft</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Sort By</label>
                <select
                  name="sortBy"
                  value={filters.sortBy}
                  onChange={handleFilterChange}
                >
                  <option value="createdAt">Date Created</option>
                  <option value="name">Name</option>
                  <option value="status">Status</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Sort Order</label>
                <select
                  name="sortOrder"
                  value={filters.sortOrder}
                  onChange={handleFilterChange}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              
              <div className="filter-actions">
                <button className="btn btn-sm btn-outline" onClick={clearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading campaigns...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="campaigns-list">
          {campaigns.map(campaign => (
            <div key={campaign._id} className="campaign-card">
              <div className="campaign-card-header">
                <h3 className="campaign-name">{campaign.name}</h3>
                <div className={`campaign-status ${getCampaignStatusClass(campaign.status)}`}>
                  {campaign.status}
                </div>
              </div>
              
              <div className="campaign-stats">
                <div className="stat-item">
                  <div className="stat-value">{campaign.analytics?.sent || 0}</div>
                  <div className="stat-label">Sent</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {campaign.analytics?.opened
                      ? `${Math.round((campaign.analytics.opened / campaign.analytics.sent) * 100)}%`
                      : '0%'
                    }
                  </div>
                  <div className="stat-label">Open Rate</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {campaign.analytics?.clicked
                      ? `${Math.round((campaign.analytics.clicked / campaign.analytics.sent) * 100)}%`
                      : '0%'
                    }
                  </div>
                  <div className="stat-label">Click Rate</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{campaign.analytics?.replied || 0}</div>
                  <div className="stat-label">Replies</div>
                </div>
              </div>
              
              <div className="campaign-schedule">
                <div className="schedule-dates">
                  <span>Start: {new Date(campaign.schedule?.startDate).toLocaleDateString()}</span>
                  {campaign.schedule?.endDate && (
                    <span>End: {new Date(campaign.schedule.endDate).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="campaign-leads">
                  <span>{campaign.leads?.length || 0} leads</span>
                </div>
              </div>
              
              <div className="campaign-actions">
                <Link to={`/campaigns/${campaign._id}`} className="btn btn-sm btn-outline">
                  <FiEye className="btn-icon" />
                  View
                </Link>
                
                <Link to={`/campaigns/${campaign._id}/edit`} className="btn btn-sm btn-outline">
                  <FiEdit2 className="btn-icon" />
                  Edit
                </Link>
                
                {campaign.status === 'Active' ? (
                  <button 
                    className="btn btn-sm btn-outline btn-warning"
                    onClick={() => handleStatusChange(campaign._id, 'Paused')}
                  >
                    <FiPause className="btn-icon" />
                    Pause
                  </button>
                ) : campaign.status === 'Paused' || campaign.status === 'Draft' ? (
                  <button 
                    className="btn btn-sm btn-outline btn-success"
                    onClick={() => handleStatusChange(campaign._id, 'Active')}
                  >
                    <FiPlay className="btn-icon" />
                    Activate
                  </button>
                ) : null}
                
                <button 
                  className="btn btn-sm btn-outline btn-danger"
                  onClick={() => handleDeleteCampaign(campaign._id)}
                >
                  <FiTrash2 className="btn-icon" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignList;